import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ContextPane,
  safeErrorCode,
  type ContextPaneSurface,
  type ContextPaneSurfaceId,
  type ProductLocale,
} from "@smart-shikka/react";
import {
  api,
  type BrowserArtifactRow,
  type BrowserLeaseRow,
  type CodeControlLeaseRow,
  type ControlAuditRow,
  type ControlJobRow,
  type JarvisConfig,
} from "../api.js";
import { WebCaptureControls } from "./WebCaptureControls.js";

interface WebControlContextPaneProps {
  token: string;
  locale: ProductLocale;
  projectId?: string | undefined;
  onClose: () => void;
}

interface RuntimeSnapshot {
  config: JarvisConfig | null;
  jobs: ControlJobRow[];
  events: ControlAuditRow[];
  leases: BrowserLeaseRow[];
  artifacts: BrowserArtifactRow[];
  worktreeLeases: CodeControlLeaseRow[];
  errors: string[];
  loadedAt: number;
}

const EMPTY_SNAPSHOT: RuntimeSnapshot = {
  config: null,
  jobs: [],
  events: [],
  leases: [],
  artifacts: [],
  worktreeLeases: [],
  errors: [],
  loadedAt: 0,
};

function text(locale: ProductLocale, en: string, hi: string): string {
  return locale === "hi" ? hi : en;
}

function formatTime(value: number, locale: ProductLocale): string {
  if (!Number.isFinite(value) || value <= 0) return "—";
  return new Date(value).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function StatusPill({ active, children }: { active: boolean; children: string }) {
  return <span className={`ss-control-status${active ? " is-active" : ""}`}>{children}</span>;
}

function EmptyState({ locale, message }: { locale: ProductLocale; message?: string }) {
  return (
    <p className="ss-control-empty">
      {message ?? text(locale, "No real runtime activity is available.", "अभी वास्तविक runtime activity उपलब्ध नहीं है।")}
    </p>
  );
}

function TerminalSurface({ locale }: { locale: ProductLocale }) {
  return (
    <div className="ss-control-section">
      <EmptyState
        locale={locale}
        message={text(
          locale,
          "Embedded terminal is available in the desktop app.",
          "एम्बेडेड टर्मिनल डेस्कटॉप ऐप में उपलब्ध है।",
        )}
      />
    </div>
  );
}

function RuntimeErrors({ locale, errors }: { locale: ProductLocale; errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <div className="ss-control-error" role="status">
      <strong>{text(locale, "Not connected", "कनेक्ट नहीं है")}</strong>
      <span>{errors.join(" · ")}</span>
    </div>
  );
}

function CurrentWork({
  locale,
  snapshot,
}: {
  locale: ProductLocale;
  snapshot: RuntimeSnapshot;
}) {
  const activeJobs = snapshot.jobs.filter((job) =>
    ["pending", "queued", "running", "executing", "active", "paused_for_approval", "waiting_for_approval"].includes(job.state),
  );

  return (
    <div className="ss-control-stack">
      <RuntimeErrors locale={locale} errors={snapshot.errors} />

      <section className="ss-control-section">
        <header>
          <strong>{text(locale, "Current work", "वर्तमान कार्य")}</strong>
          <span>{activeJobs.length}</span>
        </header>
        {activeJobs.length === 0 ? (
          <EmptyState locale={locale} message={text(locale, "No task is currently running.", "अभी कोई कार्य नहीं चल रहा है।")} />
        ) : (
          activeJobs.slice(0, 8).map((job) => (
            <article className="ss-control-item" key={job.jobId}>
              <div>
                <strong>{job.action || job.capabilityId}</strong>
                <StatusPill active={job.state === "running" || job.state === "active"}>{job.state}</StatusPill>
              </div>
              <span>{job.capabilityId}</span>
              <small>{formatTime(job.startedAt, locale)}</small>
            </article>
          ))
        )}
      </section>
    </div>
  );
}

function BrowserAndDesktop({
  locale,
  snapshot,
}: {
  locale: ProductLocale;
  snapshot: RuntimeSnapshot;
}) {
  const latestDesktop = [...snapshot.events]
    .sort((a, b) => b.timestamp - a.timestamp)
    .find((event) => `${event.agent} ${event.summary}`.toLowerCase().includes("desktop"));
  const activeLeases = snapshot.leases.filter((lease) =>
    ["pending", "active", "paused_for_approval"].includes(lease.state),
  );

  return (
    <div className="ss-control-stack">
      <RuntimeErrors locale={locale} errors={snapshot.errors} />
      <section className="ss-control-section">
        <header><strong>Browser Control</strong></header>
        <div className="ss-control-kv">
          <span>{text(locale, "Setting", "सेटिंग")}</span>
          <StatusPill active={snapshot.config?.browserControl === true}>
            {snapshot.config ? (snapshot.config.browserControl ? text(locale, "Enabled", "चालू") : text(locale, "Disabled", "बंद")) : text(locale, "Unavailable", "उपलब्ध नहीं")}
          </StatusPill>
          <span>{text(locale, "Active VPS sessions", "चालू VPS sessions")}</span>
          <b>{activeLeases.length}</b>
        </div>
        {activeLeases.length === 0 ? (
          <EmptyState locale={locale} message={text(locale, "No browser action is running.", "अभी कोई browser action नहीं चल रहा है।")} />
        ) : (
          activeLeases.map((lease) => (
            <article className="ss-control-item" key={lease.leaseId}>
              <div>
                <strong>{lease.capabilityId}</strong>
                <StatusPill active={lease.state === "active"}>{lease.state}</StatusPill>
              </div>
              <span>{lease.controllerType} · {lease.sessionId}</span>
              <small>{text(locale, "Heartbeat", "Heartbeat")}: {formatTime(lease.heartbeatAt, locale)}</small>
            </article>
          ))
        )}
      </section>

      <section className="ss-control-section">
        <header><strong>Desktop Control</strong></header>
        <div className="ss-control-kv">
          <span>{text(locale, "Setting", "सेटिंग")}</span>
          <StatusPill active={snapshot.config?.desktopControl === "readonly"}>
            {snapshot.config?.desktopControl ?? text(locale, "Unavailable", "उपलब्ध नहीं")}
          </StatusPill>
        </div>
        {latestDesktop ? (
          <article className="ss-control-item">
            <div>
              <strong>{latestDesktop.agent}</strong>
              <StatusPill active={latestDesktop.event === "started"}>{latestDesktop.event}</StatusPill>
            </div>
            <span>{latestDesktop.summary}</span>
            <small>{formatTime(latestDesktop.timestamp, locale)}</small>
          </article>
        ) : (
          <EmptyState locale={locale} message={text(locale, "No desktop activity is available.", "अभी desktop activity उपलब्ध नहीं है।")} />
        )}
      </section>
    </div>
  );
}

function TaskProgress({
  locale,
  snapshot,
}: {
  locale: ProductLocale;
  snapshot: RuntimeSnapshot;
}) {
  const items = [...snapshot.jobs].sort((a, b) => b.startedAt - a.startedAt);
  return (
    <div className="ss-control-stack">
      <RuntimeErrors locale={locale} errors={snapshot.errors} />
      <section className="ss-control-section">
        <header>
          <strong>{text(locale, "Task progress", "कार्य प्रगति")}</strong>
          <span>{items.length}</span>
        </header>
        {items.length === 0 ? (
          <EmptyState locale={locale} message={text(locale, "No task has been recorded yet.", "अभी तक कोई कार्य दर्ज नहीं हुआ है।")} />
        ) : (
          items.slice(0, 20).map((job) => (
            <article className="ss-control-item" key={job.jobId}>
              <div>
                <strong>{job.action || job.capabilityId}</strong>
                <StatusPill active={job.state === "running" || job.state === "active"}>{job.state}</StatusPill>
              </div>
              <span>{job.capabilityId}{job.waitingForApproval ? text(locale, " · awaiting approval", " · अनुमति की प्रतीक्षा") : ""}</span>
              <small>{formatTime(job.startedAt, locale)}{typeof job.remainingMs === "number" && job.remainingMs > 0 ? ` · ${Math.ceil(job.remainingMs / 1000)}s` : ""}</small>
            </article>
          ))
        )}
      </section>
    </div>
  );
}

function BrowserTabs({
  locale,
  snapshot,
}: {
  locale: ProductLocale;
  snapshot: RuntimeSnapshot;
}) {
  const [selectedLeaseId, setSelectedLeaseId] = useState<string | undefined>(undefined);
  const activeLeases = snapshot.leases.filter((lease) =>
    ["pending", "active", "paused_for_approval"].includes(lease.state),
  );
  const selected = activeLeases.find((lease) => lease.leaseId === selectedLeaseId) ?? activeLeases[0];

  return (
    <div className="ss-control-stack">
      <RuntimeErrors locale={locale} errors={snapshot.errors} />
      <section className="ss-control-section">
        <header>
          <strong>{text(locale, "Browser sessions", "ब्राउज़र सेशन")}</strong>
          <span>{activeLeases.length}</span>
        </header>
        {activeLeases.length === 0 ? (
          <EmptyState locale={locale} message={text(locale, "No controlled browser tab is open.", "कोई controlled browser tab खुला नहीं है।")} />
        ) : (
          <>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} role="tablist">
              {activeLeases.map((lease, index) => {
                const isActive = selected?.leaseId === lease.leaseId;
                return (
                  <button
                    key={lease.leaseId}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setSelectedLeaseId(lease.leaseId)}
                    style={{
                      fontSize: 10,
                      padding: "3px 8px",
                      borderRadius: 999,
                      border: `1px solid ${isActive ? "var(--ss-primary, #6d5ef3)" : "var(--ss-border, #ddd)"}`,
                      background: isActive ? "var(--ss-chip, #f0eeff)" : "transparent",
                      color: "inherit",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {index + 1} · {lease.controllerType}
                  </button>
                );
              })}
            </div>
            {selected ? (
              <article className="ss-control-item">
                <div>
                  <strong>{selected.capabilityId}</strong>
                  <StatusPill active={selected.state === "active"}>{selected.state}</StatusPill>
                </div>
                <span>{selected.controllerType} · {selected.sessionId}</span>
                <small>{text(locale, "Heartbeat", "Heartbeat")}: {formatTime(selected.heartbeatAt, locale)} · {text(locale, "Expires", "समाप्ति")}: {formatTime(selected.expiresAt, locale)}</small>
              </article>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}

function WorkingTree({
  locale,
  snapshot,
}: {
  locale: ProductLocale;
  snapshot: RuntimeSnapshot;
}) {
  const items = [...snapshot.worktreeLeases].sort((a, b) => b.createdAtMs - a.createdAtMs);
  return (
    <div className="ss-control-stack">
      <RuntimeErrors locale={locale} errors={snapshot.errors} />
      <section className="ss-control-section">
        <header>
          <strong>{text(locale, "Sandbox worktrees", "सैंडबॉक्स वर्कट्री")}</strong>
          <span>{items.length}</span>
        </header>
        {items.length === 0 ? (
          <EmptyState locale={locale} message={text(locale, "No active worktree lease.", "कोई सक्रिय वर्कट्री lease नहीं।")} />
        ) : (
          items.map((lease) => (
            <article className="ss-control-item" key={lease.leaseId}>
              <div>
                <strong title={lease.worktreePath}>{lease.worktreePath.split("/").filter(Boolean).pop() ?? lease.worktreePath}</strong>
                <StatusPill active={lease.publishedCommit !== undefined}>{lease.publishedCommit ? text(locale, "published", "published") : "active"}</StatusPill>
              </div>
              <span>{lease.action}</span>
              <small>{formatTime(lease.createdAtMs, locale)} · {lease.baseCommit.slice(0, 8)}</small>
            </article>
          ))
        )}
      </section>
      <section className="ss-control-section">
        <header>
          <strong>{text(locale, "Git Pilot", "गिट पायलट")}</strong>
        </header>
        <EmptyState
          locale={locale}
          message={text(
            locale,
            "Sandbox patch/commit is available in the desktop app.",
            "सैंडबॉक्स patch/commit डेस्कटॉप ऐप में उपलब्ध है।",
          )}
        />
      </section>
    </div>
  );
}

function EvidenceList({ locale, artifacts }: { locale: ProductLocale; artifacts: BrowserArtifactRow[] }) {
  if (artifacts.length === 0) {
    return <EmptyState locale={locale} message={text(locale, "No VPS capture evidence is available.", "VPS पर अभी capture evidence उपलब्ध नहीं है।")} />;
  }
  return (
    <div className="ss-control-list">
      {[...artifacts]
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 12)
        .map((artifact) => (
          <article className="ss-control-item" key={artifact.artifactId}>
            <div>
              <strong>{artifact.filename}</strong>
              <StatusPill active={artifact.status === "active"}>{artifact.source}</StatusPill>
            </div>
            <span>{artifact.mimeType} · {Math.max(0, Math.round(artifact.size / 1024))} KB</span>
            <small>{formatTime(artifact.createdAt, locale)}</small>
          </article>
        ))}
    </div>
  );
}

function Captures({ locale, snapshot }: { locale: ProductLocale; snapshot: RuntimeSnapshot }) {
  return (
    <div className="ss-control-stack">
      <section className="ss-control-section">
        <header><strong>{text(locale, "Screenshot and recording", "स्क्रीनशॉट और रिकॉर्डिंग")}</strong></header>
        <WebCaptureControls />
      </section>
      <section className="ss-control-section">
        <header><strong>{text(locale, "VPS evidence", "VPS evidence")}</strong></header>
        <EvidenceList locale={locale} artifacts={snapshot.artifacts} />
      </section>
    </div>
  );
}

function AgentActivity({ locale, snapshot }: { locale: ProductLocale; snapshot: RuntimeSnapshot }) {
  const items = [...snapshot.events].sort((a, b) => b.timestamp - a.timestamp);
  return (
    <div className="ss-control-stack">
      <RuntimeErrors locale={locale} errors={snapshot.errors} />
      <section className="ss-control-section">
        <header>
          <strong>{text(locale, "Agent activity", "एजेंट गतिविधि")}</strong>
          <span>{items.length}</span>
        </header>
        {items.length === 0 ? (
          <EmptyState locale={locale} />
        ) : (
          items.slice(0, 20).map((event) => (
            <article className="ss-control-item" key={event.id}>
              <div>
                <strong>{event.agent}</strong>
                <StatusPill active={event.event === "started"}>{event.event}</StatusPill>
              </div>
              <span>{event.summary}</span>
              <small>{formatTime(event.timestamp, locale)}</small>
            </article>
          ))
        )}
      </section>
    </div>
  );
}

export function WebControlContextPane(props: WebControlContextPaneProps) {
  const [activeSurfaceId, setActiveSurfaceId] = useState<ContextPaneSurfaceId>("activity");
  const [snapshot, setSnapshot] = useState<RuntimeSnapshot>(EMPTY_SNAPSHOT);

  const refresh = useCallback(async () => {
    const configPromise = api.jarvisConfig(props.token);
    const artifactsPromise = api.browserArtifacts(props.token);
    const jobsPromise = props.projectId ? api.controlJobs(props.token, props.projectId) : Promise.resolve(null);
    const auditPromise = props.projectId ? api.controlAuditTimeline(props.token, props.projectId) : Promise.resolve(null);
    const leasesPromise = props.projectId ? api.browserSessionLeases(props.token, props.projectId) : Promise.resolve(null);
    const worktreePromise = props.projectId ? api.codeControlLeases(props.token, props.projectId) : Promise.resolve(null);
    const [config, artifacts, jobs, audit, leases, worktree] = await Promise.all([
      configPromise,
      artifactsPromise,
      jobsPromise,
      auditPromise,
      leasesPromise,
      worktreePromise,
    ]);

    const errors: string[] = [];
    const errLine = (label: string, result: { ok: boolean; error?: string | null; status?: number | null }): void => {
      const code = safeErrorCode(result.error ?? result.status);
      errors.push(`${label}: ${code === "generic_error" ? text(props.locale, "unavailable", "उपलब्ध नहीं") : code}`);
    };
    if (!config.ok) errLine("CEO", config);
    if (!artifacts.ok) errLine("Evidence", artifacts);
    if (!props.projectId) errors.push(text(props.locale, "Project context unavailable", "Project context उपलब्ध नहीं"));
    if (jobs && !jobs.ok) errLine("Jobs", jobs);
    if (audit && !audit.ok) errLine("Activity", audit);
    if (leases && !leases.ok) errLine("Browser", leases);
    if (worktree && !worktree.ok) errLine(text(props.locale, "Worktree", "वर्कट्री"), worktree);

    setSnapshot({
      config: config.ok ? config.data?.config ?? null : null,
      artifacts: artifacts.ok ? artifacts.data?.artifacts ?? [] : [],
      jobs: jobs?.ok ? jobs.data?.jobs ?? [] : [],
      events: audit?.ok ? audit.data?.events ?? [] : [],
      leases: leases?.ok ? leases.data?.leases ?? [] : [],
      worktreeLeases: worktree?.ok ? worktree.data?.leases ?? [] : [],
      errors,
      loadedAt: Date.now(),
    });
  }, [props.locale, props.projectId, props.token]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 6000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const surfaces = useMemo<ContextPaneSurface[]>(
    () => [
      {
        id: "activity",
        render: () => <CurrentWork locale={props.locale} snapshot={snapshot} />,
      },
      {
        id: "taskProgress",
        render: () => <TaskProgress locale={props.locale} snapshot={snapshot} />,
      },
      {
        id: "browser",
        render: () => <BrowserTabs locale={props.locale} snapshot={snapshot} />,
      },
      {
        id: "terminal",
        render: () => <TerminalSurface locale={props.locale} />,
      },
      {
        id: "controls",
        render: () => <BrowserAndDesktop locale={props.locale} snapshot={snapshot} />,
      },
      {
        id: "workingTree",
        render: () => <WorkingTree locale={props.locale} snapshot={snapshot} />,
      },
      {
        id: "captures",
        render: () => <Captures locale={props.locale} snapshot={snapshot} />,
      },
      {
        id: "agents",
        render: () => <AgentActivity locale={props.locale} snapshot={snapshot} />,
      },
    ],
    [props.locale, snapshot],
  );

  return (
    <ContextPane
      locale={props.locale}
      surfaces={surfaces}
      activeSurfaceId={activeSurfaceId}
      onActiveSurfaceChange={setActiveSurfaceId}
      onClose={props.onClose}
      initialWidth={380}
      storageKey="ss.webControlPaneWidth.v1"
    />
  );
}
